import { MenuPermission, PermissionType, User } from './domain/rbac/RbacDomains';
import { dispatch, store } from './redux/store';
import { AccessBase, AccessCollaboratorJsonModel } from './domain/access/BaseAccess';
import { setAccess } from './redux/actions/UserActions';
import { notUndefined } from './util/Assert';
import ComponentCategoryUtil from './util/ComponenyCategoryUtil';
import ComponentName from './constant/ComponentName';

class RoleHelper {
  private static roles: MenuPermission[] = [];
  private static user: User;
  private static freezed: boolean = false;
  private static accessRole: PermissionType | undefined = undefined;
  private static isCollaboratorAccess: boolean = false;
  public static readonly roleValues: Record<PermissionType, number> = {
    'No Permission': 0,
    View: 1,
    Edit: 2,
  };

  public static setRoles(roles: MenuPermission[]) {
    this.user = store.getState().auth.user;
    this.roles = roles;
  }

  public static isAdmin(): boolean {
    if (this.user == undefined) {
      return false;
    }
    return this.user.role === 'Admin'
      || this.user.role === 'Root'
      || this.user.roles.includes('Admin')
      || (this.user.groupRoles ?? []).includes('Admin');
  }

  public static freeze() {
    this.freezed = true;
  }

  public static unfreeze() {
    this.freezed = false;
  }

  /**
   * Call this when you entered a component
   */

  public static setCurrentAccess(access: AccessBase | PermissionType | undefined) {
    if (typeof access == 'string') {
      this.accessRole = access;
      this.isCollaboratorAccess = false;
      return;
    }
    if (access == undefined) {
      this.accessRole = undefined;
      this.isCollaboratorAccess = false;
      dispatch(
        setAccess({
          component: null,
          role: null,
        }),
      );
      return;
    }
    if (this.isAdmin()) {
      this.isCollaboratorAccess = false;
      this.accessRole = 'Edit';
      dispatch(
        setAccess({
          component: access,
          role: this.accessRole,
        }),
      );
      return;
    }
    let isCollaborator = false;
    const collaboratorRole = access.collaborators
      ?.filter((c) => (c.type == 'User' && c.name == this.user.username) || (c.type == 'Group' && this.user.groups.includes(c.name)))
      .reduce((a: PermissionType, b: AccessCollaboratorJsonModel) => {
        isCollaborator = true;
        return (this.roleValues[a] || 0) < (this.roleValues[b.role] || 0) ? b.role : a;
      }, 'No Permission') || 'No Permission';
    if (isCollaborator) {
      this.isCollaboratorAccess = true;
      this.accessRole = collaboratorRole;
      dispatch(
        setAccess({
          component: access,
          role: this.accessRole,
        }),
      );
      return;
    }
    // Not invited: Configuration Access does not apply; global role is used as-is.
    this.isCollaboratorAccess = false;
    this.accessRole = undefined;
    dispatch(
      setAccess({
        component: access,
        role: null,
      }),
    );
  }

  public static clearCurrentAccess() {
    dispatch(setAccess(undefined));
    this.accessRole = undefined;
    this.isCollaboratorAccess = false;
    this.freezed = false;
  }

  public static canView(menu: string): boolean {
    if (this.isAdmin()) return true;
    const permissionByRole = this.getPermissionByRole(this.roles, this.resolveMenuName(menu));
    const access = this.checkAccess(permissionByRole);
    return access != 'No Permission';
  }

  public static canEdit(menu: string, allowForFreeze: boolean = false): boolean {
    if (this.freezed && !allowForFreeze) return false;
    notUndefined(menu, 'Menu cannot be undefined');
    if (this.isAdmin()) return true;
    const permissionByRole = this.getPermissionByRole(this.roles, this.resolveMenuName(menu));
    const access = this.checkAccess(permissionByRole);
    const allowed: PermissionType[] = ['Edit'];
    return allowed.includes(access);
  }

  public static canDeploy(menu: string, allowForFreeze: boolean = false): boolean {
    if (this.freezed && !allowForFreeze) return false;
    if (this.isAdmin()) return true;
    const permissionByRole = this.getPermissionByRole(this.roles, this.resolveMenuName(menu));
    const access = this.checkAccess(permissionByRole);
    const allowed: PermissionType[] = ['Edit'];
    return allowed.includes(access);
  }

  public static canManage(menu: string, allowForFreeze: boolean = false): boolean {
    if (this.freezed && !allowForFreeze) return false;
    if (this.isAdmin()) return true;
    const permissionByRole = this.getPermissionByRole(this.roles, this.resolveMenuName(menu));
    const access = this.checkAccess(permissionByRole);
    return access == 'Edit';
  }

  public static hasGlobalMenuEdit(menu: string, allowForFreeze: boolean = false): boolean {
    if (this.freezed && !allowForFreeze) return false;
    if (this.isAdmin()) return true;
    const permissionByRole = this.getPermissionByRole(this.roles, this.resolveMenuName(menu));
    return permissionByRole == 'Edit';
  }

  private static resolveMenuName(menu: string): string {
    if (this.getPermissionByRole(this.roles, menu) != 'No Permission') {
      return menu;
    }
    try {
      return ComponentCategoryUtil.getRoleNamesByComponent(menu as ComponentName);
    } catch {
      return menu;
    }
  }

  private static getPermissionByRole = (roles: MenuPermission[], role: string): PermissionType => {
    let permission: PermissionType = 'No Permission';

    if (roles == undefined) return permission;

    roles.forEach((p) => {
      if (permission == 'No Permission') {
        if (p.baseMenu == role) {
          permission = p.permission;
        }

        if (p.subMenu.length > 0 && permission == 'No Permission') {
          permission = this.getPermissionByRole(p.subMenu, role);
        }
      }
    });

    return permission;
  };

  private static checkAccess(menuRole: PermissionType): PermissionType {
    // Collaborator (or explicit accessRole): CA fully overrides global role.
    // Not invited: accessRole stays undefined → global role only.
    if (this.accessRole != undefined) {
      return this.accessRole;
    }
    return menuRole;
  }

  public static getPermission(base: AccessBase): PermissionType {
    this.setCurrentAccess(base);
    const role = this.accessRole || 'No Permission';
    this.clearCurrentAccess();
    return role;
  }
}

export default RoleHelper;
